import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { SEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import {
  ShoppingBag, Award, Flame, Zap, Crown, Star, Loader2, Check, Lock, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const ITEM_ICONS = {
  badge_vip: Crown,
  badge_pro: Star,
  badge_fire: Flame,
  highlight_deal: Zap,
  custom_title: Award,
  double_zorki: Flame,
};

export default function ShopPage() {
  const { authHeaders, refreshUser } = useAuth();
  const [items, setItems] = useState([]);
  const [userZorki, setUserZorki] = useState(0);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    fetchShop();
    fetchPurchases();
  }, []);

  const fetchShop = async () => {
    try {
      const res = await axios.get(`${API}/shop/items`, { headers: authHeaders() });
      setItems(res.data.items);
      setUserZorki(res.data.user_zorki);
    } catch {}
    setLoading(false);
  };

  const fetchPurchases = async () => {
    try {
      const res = await axios.get(`${API}/shop/purchases`, { headers: authHeaders() });
      setPurchases(res.data.purchases);
    } catch {}
  };

  const handleBuy = async (itemId) => {
    setBuying(itemId);
    try {
      const res = await axios.post(`${API}/shop/buy/${itemId}`, null, { headers: authHeaders() });
      toast.success(`Kupiono: ${res.data.item}!`);
      setUserZorki(res.data.remaining_zorki);
      refreshUser();
      fetchShop();
      fetchPurchases();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd zakupu');
    }
    setBuying(null);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-20 md:pb-6" data-testid="shop-page">
      <SEO title="Sklep nagród" description="Wydaj Zorki na nagrody, badge'e i bonusy!" />

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-white flex items-center gap-2">
            <ShoppingBag className="w-6 h-6 text-primary" /> SKLEP NAGRÓD
          </h1>
          <p className="text-sm text-muted-foreground">Wydaj Zorki na nagrody i bonusy</p>
        </div>
        <div className="bg-card border border-zorki/30 px-4 py-2 flex items-center gap-2" data-testid="shop-balance">
          <Flame className="w-5 h-5 text-zorki" />
          <span className="font-heading text-xl font-bold text-zorki">{userZorki}</span>
          <span className="text-xs text-muted-foreground">Zorków</span>
        </div>
      </div>

      {/* Items grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8" data-testid="shop-items">
        {items.map(item => {
          const Icon = ITEM_ICONS[item.id] || Award;
          const canAfford = userZorki >= item.cost;

          return (
            <div
              key={item.id}
              className={`bg-card border p-4 flex flex-col items-center text-center transition-colors ${
                item.owned
                  ? 'border-green-500/30 bg-green-500/5'
                  : canAfford
                    ? 'border-border/40 hover:border-primary/30'
                    : 'border-border/20 opacity-60'
              }`}
              data-testid={`shop-item-${item.id}`}
            >
              <div className={`w-12 h-12 flex items-center justify-center mb-3 ${
                item.owned ? 'text-green-400' : 'text-primary'
              }`}>
                <Icon className="w-8 h-8" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading font-bold text-white text-sm mb-1">{item.name}</h3>
              <p className="text-xs text-muted-foreground mb-3 leading-relaxed">{item.description}</p>

              <div className="mt-auto w-full">
                {item.owned ? (
                  <div className="flex items-center justify-center gap-1 text-green-400 text-xs font-heading uppercase">
                    <Check className="w-3.5 h-3.5" /> Posiadasz
                  </div>
                ) : (
                  <Button
                    onClick={() => handleBuy(item.id)}
                    disabled={buying === item.id || !canAfford}
                    className={`w-full rounded-none font-heading uppercase tracking-wider text-xs h-9 ${
                      canAfford
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-secondary text-muted-foreground'
                    }`}
                    data-testid={`buy-${item.id}`}
                  >
                    {buying === item.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : !canAfford ? (
                      <><Lock className="w-3 h-3 mr-1" /> {item.cost} Z</>
                    ) : (
                      <><Flame className="w-3 h-3 mr-1 text-zorki" /> {item.cost} Zorków</>
                    )}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Purchase history */}
      {purchases.length > 0 && (
        <div className="bg-card border border-border/40 p-4" data-testid="purchase-history">
          <h2 className="font-heading text-base font-bold text-white mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" /> Historia zakupów
          </h2>
          <div className="space-y-2">
            {purchases.map(p => (
              <div key={p.id} className="flex items-center justify-between py-2 border-b border-border/20 last:border-0">
                <span className="text-sm text-white">{p.item_name}</span>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-destructive font-heading font-bold">-{p.cost} Z</span>
                  <span className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleDateString('pl-PL')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
